import { AhaFeature } from '@Models/types';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { FloppyDiskBack, PencilSimple } from '@phosphor-icons/react';
import { MoonLoader } from 'react-spinners';
import DOMPurify from 'dompurify';
import { useAuth } from '@Context/CognitoAuthProvider';
import { aiGenerateAPI } from '@Lib/axios/testcases';
import { useState } from 'react';
import {
  processHtmlForEditing,
  convertToAhaChecklistHtml,
} from '@Utils/FormatEditBox';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ConfirmationModal from '@Common/ConfirmationModal';
import showToast from '@Common/toast/showToast';

const Preview = () => {
  const {
    generatedAhaTicket,
    generateAhaLoading,
    selectedRelease,
    publishedAhaLink,
    setPublishedAhaLink,
    setGeneratedAhaTicket,
  } = useGeneratorContext();
  const { logout } = useAuth();
  const aiGenerate = aiGenerateAPI(logout);

  const [publishing, setPublishing] = useState<boolean>(false);

  const formattedTicket = (() => {
    try {
      if (!generateAhaLoading && generatedAhaTicket) {
        let cleanedJson = generatedAhaTicket
          .replace(/^```json/, '')
          .replace(/```$/, '')
          .trim();

        if (cleanedJson.startsWith('"') && cleanedJson.endsWith('"')) {
          cleanedJson = cleanedJson.slice(1, -1);
          cleanedJson = cleanedJson
            .replace(/\\"/g, '"')
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\r/g, '\r')
            .replace(/\\\\/g, '\\');
        }
        try {
          return JSON.parse(cleanedJson) as AhaFeature;
        } catch (firstError) {
          console.log('First parse attempt failed:', firstError);
          cleanedJson = cleanedJson.replace(/\\"/g, '"');
          return JSON.parse(cleanedJson) as AhaFeature;
        }
      }
      return null;
    } catch (error) {
      console.error('JSON parsing error:', error);
      showToast.error('Something went wrong. Please try again.');
      return null;
    }
  })();

  const handlePublish = async () => {
    setPublishing(true);
    if (!selectedRelease || generateAhaLoading || !formattedTicket) {
      setPublishing(false);
      return;
    }
    const reference = await aiGenerate.publishAhaTicket(
      formattedTicket,
      selectedRelease.reference_num
    );
    setPublishedAhaLink(
      `https://trajector1.aha.io/develop/features/${reference}`
    );
    setPublishing(false);
    showToast.success(
      'Your ticket has now been published to Aha! please check the link provided.'
    );
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editableDescription, setEditableDescription] = useState('');
  const [editableTitle, setEditableTitle] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSave = () => {
    if (!formattedTicket) return;

    const restoredDescription = convertToAhaChecklistHtml(editableDescription);

    const updatedTicket = {
      ...formattedTicket,
      name: editableTitle,
      description: restoredDescription,
    };

    setGeneratedAhaTicket(JSON.stringify(updatedTicket));
    setIsEditing(false);
  };

  const handleEditButton = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      setEditableTitle(formattedTicket?.name || '');
      const processedDescription = processHtmlForEditing(
        formattedTicket?.description || ''
      );
      setEditableDescription(processedDescription);
      setIsEditing(true);
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col gap-y-2 overflow-scroll rounded-lg bg-white p-4">
        <div className="mb-5 flex flex-row justify-between">
          <span className="font-extrabold text-primary-700">Preview</span>
          <div className="flex flex-row gap-2">
            <button
              disabled={
                !selectedRelease ||
                generateAhaLoading ||
                publishing ||
                !generatedAhaTicket ||
                publishedAhaLink !== null
              }
              className="flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-3 py-2 text-xs text-white hover:cursor-pointer disabled:bg-gray-400"
              onClick={handleEditButton}
            >
              <PencilSimple size={15} />
              {isEditing && !generateAhaLoading ? (
                <span>Cancel</span>
              ) : (
                <span>Edit</span>
              )}
            </button>
            {isEditing && (
              <button
                className="flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-3 py-2 text-xs text-white hover:cursor-pointer"
                onClick={handleSave}
              >
                <FloppyDiskBack size={15} />
                <span>Save Edit</span>
              </button>
            )}
            <button
              disabled={
                !selectedRelease ||
                generateAhaLoading ||
                publishing ||
                !generatedAhaTicket ||
                publishedAhaLink !== null ||
                isEditing
              }
              className="flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-3 py-2 text-xs text-white hover:cursor-pointer disabled:bg-gray-400"
              onClick={() => setIsModalOpen(true)}
            >
              <FloppyDiskBack size={15} />
              <span>Publish</span>
            </button>
          </div>
          <ConfirmationModal
            isOpen={isModalOpen}
            title="Publish Ticket"
            message="Are you sure you want to publish this ticket to Aha?"
            loading={publishing}
            confirmButtonStyle={{ label: 'Publish', color: 'NEUTRAL' }}
            cancelButtonStyle={{ label: 'Cancel', color: 'NONE' }}
            onConfirm={async () => {
              setIsModalOpen(false);
              await handlePublish();
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </div>
        {generateAhaLoading && (
          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
            <MoonLoader color="#071D49" />
            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
              Generating Ticket
            </span>
          </span>
        )}

        {publishing && (
          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
            <MoonLoader color="#071D49" />
            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
              Publishing Ticket
            </span>
          </span>
        )}

        {!generatedAhaTicket && !generateAhaLoading && (
          <div className="text-xs">
            No preview available. Please generate a ticket first.
          </div>
        )}

        {generatedAhaTicket && !generateAhaLoading && (
          <>
            {!publishing && !publishedAhaLink && (
              <>
                {isEditing ? (
                  <input
                    className="mb-2 border-b border-gray-300 text-2xl font-extrabold text-primary-700 focus:border-primary-700 focus:outline-none"
                    value={editableTitle}
                    onChange={(e) => setEditableTitle(e.target.value)}
                  />
                ) : (
                  <div className="text-xl font-bold">
                    {formattedTicket?.name}
                  </div>
                )}
                <div className="flex flex-col">
                  {isEditing ? (
                    <ReactQuill
                      theme="snow"
                      value={editableDescription}
                      onChange={setEditableDescription}
                      style={{ minHeight: 200, marginBottom: 16 }}
                    />
                  ) : (
                    <div
                      className="adf-body"
                      dangerouslySetInnerHTML={{
                        // Aha-sourced HTML; DOMPurify defaults are sufficient here.
                        __html: DOMPurify.sanitize(
                          formattedTicket?.description || ''
                        ),
                      }}
                    />
                  )}
                </div>
              </>
            )}

            {!publishing && publishedAhaLink && (
              <div className="flex flex-col gap-2">
                <label>Published Link: </label>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm italic text-primary-700 hover:text-primary-300"
                  href={publishedAhaLink}
                >
                  {publishedAhaLink}
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Preview;
